import { useContext, useRef, type ComponentProps, type ReactNode } from "react";

import { customTv } from "#utils";
import {
  Button,
  type ButtonProps,
  OverlayArrow,
  Tooltip as ReactAriaTooltip,
  TooltipTrigger,
  TooltipTriggerStateContext,
} from "react-aria-components";

type SimplePlacement = "top" | "right" | "bottom" | "left";

type ReactAriaTooltipProps = ComponentProps<typeof ReactAriaTooltip>;
type ReactAriaTooltipTriggerProps = ComponentProps<typeof TooltipTrigger>;

export interface TooltipProps
  extends
    Omit<ReactAriaTooltipProps, "children" | "className" | "placement">,
    Omit<ReactAriaTooltipTriggerProps, "children"> {
  /** Content to render inside the trigger button */
  children?: ReactNode;
  /** Content to display in the tooltip */
  title: ReactNode;
  /** Position of the tooltip relative to the trigger, only SimplePlacement is supported as the arrow SVG only renders in cardinal placements */
  placement?: SimplePlacement;
  /** Optional className for styling the tooltip content */
  className?: string;
  /** Data test id for the tooltip content */
  "data-testid"?: string;
  /** Optional props to pass to the trigger button */
  buttonProps?: ButtonProps;
  /**
   * When true, the child element is used directly as the tooltip trigger instead of being
   * wrapped in a Button. The child must be a react-aria-compatible button (e.g. the v2 Button).
   * Use this to avoid the "button within a button" warning when the child is already a button.
   */
  asChild?: boolean;
}

const tooltipStyles = customTv({
  base: "p-4 bg-background-primary-inverse text-text-primary-inverse text-label-small rounded-1 w-fit max-w-[20rem]",
  variants: {
    variant: {
      top: "mb-4",
      right: "ml-4",
      bottom: "mt-4",
      left: "mr-4",
    },
  },
  defaultVariants: {
    variant: "bottom",
  },
});

const arrowStyles = customTv({
  base: "fill-background-primary-inverse absolute",
  variants: {
    variant: {
      top: "bottom-2 left-1/2 -translate-x-1/2",
      right: "top-1/2 left-2 -translate-y-1/2",
      bottom: "top-2 left-1/2 -translate-x-1/2",
      left: "top-1/2 right-2 -translate-y-1/2",
    },
  },
  defaultVariants: {
    variant: "bottom",
  },
});

function getArrowPlacement(placement: SimplePlacement) {
  switch (placement) {
    case "top":
      return <path d="M0 0 L8 8 L16 0" />;
    case "bottom":
      return <path d="M0 8 L8 0 L16 8" />;
    case "left":
      return <path d="M0 0 L8 8 L0 16" />;
    case "right":
      return <path d="M8 0 L0 8 L8 16" />;
  }
}

function SvgArrow({ placement }: { placement: SimplePlacement }) {
  if (placement === "left" || placement === "right") {
    return (
      <svg
        className={arrowStyles({
          variant: placement,
        })}
        height={16}
        viewBox="0 0 8 16"
        width={8}
      >
        {getArrowPlacement(placement)}
      </svg>
    );
  }
  return (
    <svg className={arrowStyles({ variant: placement })} height={8} viewBox="0 0 16 8" width={16}>
      {getArrowPlacement(placement)}
    </svg>
  );
}

/**
 * Wrapper for asChild mode that ensures tooltip hover detection works even when
 * the child element is disabled. Native disabled buttons suppress pointer events
 * in some browsers and React Aria's useHover bails out for disabled elements.
 * This span sits around the child and opens/closes the tooltip via
 * TooltipTriggerStateContext, bypassing those restrictions.
 */
function AsChildWrapper({
  children,
  isTooltipDisabled,
  wrapperRef,
}: {
  children: ReactNode;
  isTooltipDisabled?: boolean;
  wrapperRef: React.RefObject<HTMLSpanElement | null>;
}) {
  const state = useContext(TooltipTriggerStateContext);

  return (
    <span
      // max-w-full keeps the trigger (the tooltip anchor) inside its
      // container when the child overflows, so truncated children ellipsize
      // and the tooltip centers on the visible part instead of the clipped
      // full-width content.
      className="inline-flex max-w-full"
      onPointerEnter={(e) => {
        if (e.pointerType !== "touch" && !isTooltipDisabled) {
          state?.open(false);
        }
      }}
      onPointerLeave={(e) => {
        if (e.pointerType !== "touch" && !isTooltipDisabled) {
          state?.close();
        }
      }}
      ref={wrapperRef}
    >
      {children}
    </span>
  );
}

export const Tooltip = ({
  // Custom props
  title,
  children,
  className,
  buttonProps = {},
  asChild = false,
  "data-testid": dataTestId,
  // Props with defaults
  placement = "bottom",
  delay = 300,
  containerPadding = 10,
  defaultOpen = false,
  // TooltipTrigger-specific props
  closeDelay,
  isDisabled,
  shouldCloseOnPress,
  trigger,
  isOpen,
  onOpenChange,
  // All other ReactAriaTooltip props
  ...tooltipProps
}: TooltipProps) => {
  const hasTitleContent = title !== null && title !== undefined && title !== "";
  const asChildWrapperRef = useRef<HTMLSpanElement>(null);

  if (!hasTitleContent) {
    return children;
  }

  return (
    <TooltipTrigger
      closeDelay={closeDelay}
      defaultOpen={defaultOpen}
      delay={delay}
      isDisabled={isDisabled}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      shouldCloseOnPress={shouldCloseOnPress}
      trigger={trigger}
    >
      {asChild ? (
        <AsChildWrapper isTooltipDisabled={isDisabled} wrapperRef={asChildWrapperRef}>
          {children}
        </AsChildWrapper>
      ) : (
        <Button aria-label="tooltip-trigger" {...buttonProps}>
          {children}
        </Button>
      )}
      <ReactAriaTooltip
        containerPadding={containerPadding}
        placement={placement}
        {...tooltipProps}
        {...(asChild ? { triggerRef: asChildWrapperRef } : {})}
      >
        <OverlayArrow>
          <SvgArrow placement={placement} />
        </OverlayArrow>
        <div
          aria-label={typeof title === "string" ? title : "tooltip-content"}
          className={tooltipStyles({
            variant: placement,
            className,
          })}
          data-testid={dataTestId}
        >
          {title}
        </div>
      </ReactAriaTooltip>
    </TooltipTrigger>
  );
};
